import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { CommentKind, ReviewPlanWeek } from './contracts'

/**
 * Purely local Review tab UI state — the accordion selection, the singleton
 * composer and the "attach to review" toggles. None of it is persisted by
 * `coachingV2.reviews.*`, so the live slice (`review-slice.tsx`) and the
 * fixture slice (`mock-review-slice.tsx`) share this one implementation
 * instead of keeping two copies that can drift.
 */

export interface ComposerState {
  section: string
  target: string
  kind: CommentKind
  /**
   * Stable anchor for the atom being commented on, when the opening section
   * has one that is not a line range. Same principle as `lines` below —
   * `target` is only the human label — but for sections whose atoms are
   * identified by id: the post-mortem's answers are keyed by question id,
   * because their labels are live bank wording (LG-765).
   */
  targetKey?: string
  /**
   * The code lines this comment is anchored to, when it is anchored at all
   * (LG-517). This is the anchor — `target` is only its human label. The
   * composer persists these numbers as `ReviewComment.lines`, so an edit to
   * the label's wording can never cost a comment its anchor, and a target in
   * another section that happens to end in `L{n}` can never gain one.
   */
  lines?: [number, number]
  /**
   * Identifies one composing session. Opening a DIFFERENT target starts a new
   * one; re-opening the same target, extending the range, and switching the
   * kind all keep it. The composer resets its typed draft when this changes,
   * which is the only reliable signal: a retarget within a section
   * (`Code · Overall` -> `Code · L5`) has to clear the draft, while a range
   * extension — and a second click on the affordance already open — must not.
   */
  session: number
  /**
   * The text typed so far. Here rather than in the `Composer` component
   * because the component is torn down by things that are not the end of a
   * composing session: it renders in the gutter overlay, which unmounts it
   * whenever the expanded card's sections leave the DOM — folding the week the
   * review sits in does exactly that (LG-642). Component-local, the draft died
   * with the fold; on the session, it lives exactly as long as the session
   * does, which is the rule the rest of this type already describes.
   */
  draft: string
}

/**
 * The Overall-comment block's in-progress draft, per submission. Same reason
 * as `ComposerState.draft`: the block renders inside the expanded card, so a
 * week fold unmounts it. Keyed by submission id, so a draft can never
 * resurface under a different submission than the one it was typed about.
 */
export interface OverallDraft {
  kind: CommentKind
  text: string
}

/** What an untouched Overall-comment block starts from. */
export const EMPTY_OVERALL_DRAFT: OverallDraft = { kind: 'praise', text: '' }

/**
 * Which week sections are open, and the toggle that moves one.
 *
 * Split out of {@link ReviewUiState} because the two are needed at different
 * moments: a week's rows are read only when its section is open (LG-646), so
 * the folds have to be known *before* there are any rows to hang the rest of
 * the tab's state off. The spine alone decides the default, which is the whole
 * reason this can run first.
 */
export interface WeekFoldState {
  /**
   * Whether this week's section is folded away. A week the coach has never
   * touched follows the default: the newest week the spine returned is open,
   * every older week is closed. Reading it through a function rather than off
   * a map is what keeps that default in one place — a raw map of overrides
   * says nothing about the weeks nobody has touched, which is most of them.
   */
  isWeekCollapsed: (weekKey: string) => boolean
  /**
   * Folds or unfolds one week section. Folding one that holds the open review
   * takes the review off screen with it — `ReviewUiState.expandedId` is
   * derived through this, so no caller has to keep the two in step.
   */
  toggleWeekCollapsed: (weekKey: string) => void
}

export interface ReviewUiState {
  expandedId: string | null
  setExpandedId: (id: string | null) => void
  composer: ComposerState | null
  openComposer: (
    section: string,
    target: string,
    defaultKind: CommentKind,
    targetKey?: string,
  ) => void
  /**
   * Opens a composer anchored to code lines. Separate from `openComposer`
   * because the label and the anchor have to agree: this builds the
   * `L{a}[–L{b}]` suffix onto `labelPrefix` from `lines` itself, so no caller
   * can hand in a label that names one range and an anchor that names another.
   */
  openLineComposer: (
    section: string,
    labelPrefix: string,
    defaultKind: CommentKind,
    lines: [number, number],
  ) => void
  closeComposer: () => void
  extendComposerLines: (line: number) => void
  setComposerKind: (kind: CommentKind) => void
  /** Records a keystroke in the open composer. No-op when none is open. */
  setComposerDraft: (draft: string) => void
  /**
   * Overall-comment drafts by submission id; absent means untouched. `Partial`
   * because a submission never typed about has no entry — indexing one yields
   * `undefined`, not an empty draft.
   *
   * Never cleared on a submission switch, unlike the composer: keyed by id,
   * each draft belongs to one submission and cannot surface under another, so
   * the coach who steps over to a second card and back finds their half-typed
   * wrap-up note still there. The composer has no such key — it is a singleton
   * the gutter posts to `expandedId` — which is why that one does close.
   */
  overallDrafts: Partial<Record<string, OverallDraft>>
  setOverallDraft: (submissionId: string, draft: OverallDraft) => void
  /**
   * Partial because the map is keyed by section string and only holds keys a
   * coach has actually toggled — indexing an untouched section yields
   * `undefined`, not `false`. Declared here as well as on `ReviewSlice`, which
   * reads it: the two are one field, and only one of them saying so is how the
   * lie survives.
   */
  attachments: Partial<Record<string, boolean>>
  toggleAttachment: (key: string) => void
}

/**
 * The `L{a}` / `L{a}–L{b}` suffix a code-line composer target carries (e.g.
 * 'Code · L5', 'Code · L5–L8'). Display only — the anchor itself rides along
 * as `ComposerState.lines`.
 */
const LINE_RANGE_PATTERN = /L(\d+)(?:–L(\d+))?$/

/**
 * The one producer of a line-anchored label. Both entry points below go
 * through it, so the label always spells the anchor it ships with.
 */
function lineTargetLabel(prefix: string, [lo, hi]: [number, number]): string {
  return lo === hi ? `${prefix}L${lo}` : `${prefix}L${lo}–L${hi}`
}

/**
 * Rewrites a target's `L{a}[–L{b}]` suffix to name `lines`, per spec §8.2's
 * "click a second line to extend the range". A target with no suffix is
 * returned unchanged — the caller decides what to do about that.
 */
function extendLineTarget(target: string, lines: [number, number]): string {
  const match = target.match(LINE_RANGE_PATTERN)
  // `index` is only ever undefined on a global regex, which this is not — but
  // `slice` would read it as "to the end of the string" and quietly duplicate
  // the suffix, so it is stated rather than absorbed.
  if (match?.index === undefined) return target
  return lineTargetLabel(target.slice(0, match.index), lines)
}

/**
 * Re-opening the very same target is not a new composing session: the draft on
 * screen is about exactly this target — the coach clicked "+ Comment" twice —
 * so it survives. Only a genuine retarget mints a session, which is what
 * clears the draft.
 */
function openedComposer(
  current: ComposerState | null,
  next: Omit<ComposerState, 'session' | 'draft'>,
): ComposerState {
  if (
    current !== null &&
    current.section === next.section &&
    current.target === next.target &&
    // The anchor is part of the identity, not just the label: two post-mortem
    // answers whose questions have both left the bank render the same label
    // ("Answer"), so without this they would count as one composing session
    // and the second would silently inherit the first one's anchor — the very
    // collision `targetKey` exists to stop.
    current.targetKey === next.targetKey &&
    current.lines?.[0] === next.lines?.[0] &&
    current.lines?.[1] === next.lines?.[1]
  ) {
    return current
  }
  return { ...next, session: (current?.session ?? 0) + 1, draft: '' }
}

/**
 * `defaultExpandedId` is what the accordion falls back to until the coach
 * picks a card — the live slice passes the first queue row, which only exists
 * once the queue query resolves. Tracking "the coach chose" separately from
 * "which id" is what lets `setExpandedId(null)` collapse everything without
 * the fallback immediately re-expanding the first row.
 *
 * `weekKeyOf` says which week a submission is in, or null for one the caller
 * no longer knows about; `isWeekCollapsed` comes from {@link useWeekFolds}.
 * Together they keep the accordion and the folds honest with each other: a
 * folded section hides its cards, and `expandedId` is what the rest of the tab
 * positions the gutter overlay and the floating composer against
 * (`useGutterLayout`'s deps contract; `post()` targets it), so an `expandedId`
 * naming a card inside a folded week is a comment landing on a submission the
 * coach cannot see.
 *
 * The rule is *derived* on every render rather than applied when a week is
 * folded, because folding is not the only way a card gets there: the queue
 * re-groups under a standing fold whenever an assignment is rescheduled or a
 * lifecycle command re-reads it, with no fold event to react to. Derived, it
 * holds for a chosen card, for the fallback, and for a queue that moved
 * underneath both — and unfolding a week gives the coach back the card they
 * had open in it.
 */
export function useReviewUiState(
  defaultExpandedId: string | null,
  weekKeyOf: (submissionId: string) => string | null,
  isWeekCollapsed: (weekKey: string) => boolean,
): ReviewUiState {
  const [expanded, setExpanded] = useState<{
    chosen: boolean
    id: string | null
  }>({ chosen: false, id: null })
  const [composer, setComposer] = useState<ComposerState | null>(null)
  const [attachments, setAttachments] = useState<
    Partial<Record<string, boolean>>
  >({})
  const [overallDrafts, setOverallDrafts] = useState<
    Partial<Record<string, OverallDraft>>
  >({})

  const chosenId = expanded.chosen ? expanded.id : defaultExpandedId
  const chosenWeekKey = chosenId === null ? null : weekKeyOf(chosenId)
  const expandedId =
    chosenWeekKey !== null && isWeekCollapsed(chosenWeekKey) ? null : chosenId

  const setExpandedId = useCallback((id: string | null) => {
    setExpanded({ chosen: true, id })
  }, [])

  const openComposer = useCallback(
    (
      section: string,
      target: string,
      defaultKind: CommentKind,
      targetKey?: string,
    ) => {
      setComposer((current) =>
        openedComposer(current, {
          section,
          target,
          kind: defaultKind,
          targetKey,
        }),
      )
    },
    [],
  )

  const openLineComposer = useCallback(
    (
      section: string,
      labelPrefix: string,
      defaultKind: CommentKind,
      lines: [number, number],
    ) => {
      setComposer((current) =>
        openedComposer(current, {
          section,
          target: lineTargetLabel(labelPrefix, lines),
          kind: defaultKind,
          lines,
        }),
      )
    },
    [],
  )

  const closeComposer = useCallback(() => setComposer(null), [])

  const extendComposerLines = useCallback((line: number) => {
    setComposer((current) => {
      // Only an already-anchored composer extends. `Code · Overall` carries no
      // lines, and widening "the whole section" to a line range would silently
      // re-aim a comment the coach wrote about something else.
      if (current === null || current.lines === undefined) return current
      const lines: [number, number] = [
        Math.min(current.lines[0], line),
        Math.max(current.lines[1], line),
      ]
      return {
        ...current,
        lines,
        target: extendLineTarget(current.target, lines),
      }
    })
  }, [])

  // Switching the kind pill keeps the session, so the typed draft survives it.
  const setComposerKind = useCallback((kind: CommentKind) => {
    setComposer((current) =>
      current === null ? current : { ...current, kind },
    )
  }, [])

  const setComposerDraft = useCallback((draft: string) => {
    setComposer((current) =>
      current === null ? current : { ...current, draft },
    )
  }, [])

  const setOverallDraft = useCallback(
    (submissionId: string, draft: OverallDraft) => {
      setOverallDrafts((current) => ({ ...current, [submissionId]: draft }))
    },
    [],
  )

  const toggleAttachment = useCallback((key: string) => {
    setAttachments((current) => ({ ...current, [key]: !current[key] }))
  }, [])

  return useMemo(
    () => ({
      expandedId,
      setExpandedId,
      composer,
      openComposer,
      openLineComposer,
      closeComposer,
      extendComposerLines,
      setComposerKind,
      setComposerDraft,
      overallDrafts,
      setOverallDraft,
      attachments,
      toggleAttachment,
    }),
    [
      attachments,
      closeComposer,
      composer,
      expandedId,
      extendComposerLines,
      openComposer,
      openLineComposer,
      overallDrafts,
      setComposerDraft,
      setComposerKind,
      setExpandedId,
      setOverallDraft,
      toggleAttachment,
    ],
  )
}

/**
 * The latest week in a spine, or null if it holds none.
 *
 * Chosen by the day it starts on, with `id` settling a tie, rather than off
 * the front of the list: "newest" is a fact about the week, and reading it
 * from position would silently follow a re-sorted response.
 */
function newestWeekId(weeks: readonly ReviewPlanWeek[]): string | null {
  const newest = weeks.reduce<ReviewPlanWeek | undefined>((best, week) => {
    if (best === undefined) return week
    if (week.startsOnKey !== best.startsOnKey) {
      return week.startsOnKey > best.startsOnKey ? week : best
    }
    return week.id > best.id ? week : best
  }, undefined)
  return newest?.id ?? null
}

/**
 * The week folds, and the default the spine implies.
 *
 * Held here rather than in the queue component, for the reason the whole
 * module exists: the review lane is mounted above the tab bar while the Review
 * tab body is unmounted on every tab switch, so state kept in the body resets
 * under a coach who steps over to Curriculum to move a card — while the
 * accordion selection beside it survives. The curriculum board keeps its own
 * week fold in its slice for the same reason.
 *
 * The map holds only weeks the coach has actually toggled. Everything else
 * follows the default-open week: the newest week the spine returned opens, the
 * rest stay closed, which is what stops a client's whole history loading at
 * once. A toggle records the opposite of the *effective* state, so the first
 * click on an untouched older week opens it rather than re-closing something
 * that was never open.
 *
 * That default is **latched**, which is the whole reason the spine is passed in
 * rather than a bare week key. The list moves under the tab: `listWeeks`
 * refetches on window focus, after every lifecycle command, and whenever the
 * Curriculum tab adds, deletes or publishes a week. Recomputed live, a spine
 * that comes back with a newer week changes what "open by default" means, and
 * the week the coach was working in — untouched, so with no override to
 * protect it — folds itself and unmounts its rows mid-review.
 *
 * The latch is held against the spine rather than blindly: a week can also be
 * *deleted*, or rebuilt under new ids when the coach changes the engagement's
 * week start. A key that no longer names a week in the spine would leave every
 * section collapsed and nothing loading, so it is dropped and the newest week
 * takes over. Growth cannot move the default; disappearance must.
 */
export function useWeekFolds(weeks: readonly ReviewPlanWeek[]): WeekFoldState {
  const [overrides, setOverrides] = useState<Partial<Record<string, boolean>>>(
    {},
  )

  const newestWeekKey = useMemo(() => newestWeekId(weeks), [weeks])
  // Latched *after* commit, never during render. React may render a tree it
  // then discards — an offscreen or interrupted pass — and a latch written in
  // render would keep that abandoned pass's week forever. Render therefore
  // reads the latch when it still names a live week, which gives the first
  // paint the same week the effect is about to record.
  const latchedDefault = useRef<string | null>(null)
  const latched = latchedDefault.current
  const openByDefault =
    latched !== null && weeks.some((week) => week.id === latched)
      ? latched
      : newestWeekKey
  useEffect(() => {
    latchedDefault.current = openByDefault
  }, [openByDefault])

  const isWeekCollapsed = useCallback(
    (weekKey: string): boolean =>
      overrides[weekKey] ?? weekKey !== openByDefault,
    [openByDefault, overrides],
  )

  const toggleWeekCollapsed = useCallback(
    (weekKey: string) => {
      setOverrides((current) => ({
        ...current,
        [weekKey]: !(current[weekKey] ?? weekKey !== openByDefault),
      }))
    },
    [openByDefault],
  )

  return useMemo(
    () => ({ isWeekCollapsed, toggleWeekCollapsed }),
    [isWeekCollapsed, toggleWeekCollapsed],
  )
}
